'use client';

import type { CategoryFilterState } from '@eliadmin/core/src/components/categories/category-query-state';
import { useRef, type KeyboardEvent } from 'react';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type Sort = CategoryFilterState['sort'];

/**
 * „Odporúčané“ is the core default order (no explicit sort) and has no dictionary key; the core
 * maps `sells` to the API sales count.
 */
const OPTIONS: { value: string; sort: Sort; textKey?: string }[] = [
  { value: 'recommended', sort: { orderBy: null, order: null } },
  { value: 'sells:desc', sort: { orderBy: 'sells', order: 'desc' }, textKey: 'text181' },
  { value: 'price:asc', sort: { orderBy: 'price', order: 'asc' }, textKey: 'text177' },
  { value: 'price:desc', sort: { orderBy: 'price', order: 'desc' }, textKey: 'text178' },
];

const KEY_STEP: Partial<Record<string, (index: number, last: number) => number>> = {
  ArrowRight: (index, last) => (index >= last ? 0 : index + 1),
  ArrowDown: (index, last) => (index >= last ? 0 : index + 1),
  ArrowLeft: (index, last) => (index <= 0 ? last : index - 1),
  ArrowUp: (index, last) => (index <= 0 ? last : index - 1),
  Home: () => 0,
  End: (_, last) => last,
};

/**
 * Sort switch in the look of the Figma „Tab“ as overridden in the listing toolbar (52 px, 2 px
 * border/subtle line under every tab, text/tertiary); phones (60438:86611) keep the plain „Tab“:
 * 47 px tabs in text/secondary over one 1 px border/subtle line. Sorting reorders the one product
 * list and there are no tab panels, so it is an ARIA radio group: one tab stop, arrows / Home / End
 * move and select.
 */
export function SortTabs({ className }: { className?: string }) {
  const { t } = useTranslations();
  const sort = useCategoryStore(s => s.filter.sort);
  const setFilter = useCategoryStore(s => s.setFilter);
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);

  const selectedIndex = OPTIONS.findIndex(
    option => option.sort.orderBy === sort.orderBy && option.sort.order === sort.order,
  );
  const select = (index: number) => {
    if (index !== selectedIndex) setFilter({ sort: OPTIONS[index].sort });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = KEY_STEP[event.key];
    if (!step) return;
    event.preventDefault();
    const index = step(selectedIndex, OPTIONS.length - 1);
    select(index);
    buttons.current[index]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label={t('text1716')}
      onKeyDown={onKeyDown}
      className={cn(
        'hide-scroll-bar flex overflow-x-auto border-b border-[#E2E4E7] md:border-b-0',
        className,
      )}
    >
      {OPTIONS.map((option, index) => {
        const selected = index === selectedIndex;
        return (
          <button
            key={option.value}
            ref={element => {
              buttons.current[index] = element;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected || (selectedIndex < 0 && index === 0) ? 0 : -1}
            onClick={() => select(index)}
            className={cn(
              'dlz-nav inline-flex h-[47px] shrink-0 items-center whitespace-nowrap border-b-2 px-4 transition-colors md:h-[52px]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#0F1012]',
              selected
                ? 'border-[#D7191F] text-[#0F1012]'
                : 'border-transparent text-[#4F545C] hover:border-[#9096A0] hover:text-[#0F1012] md:border-[#E2E4E7] md:text-[#6B7079]',
            )}
          >
            {option.textKey ? t(option.textKey) : 'Odporúčané'}
          </button>
        );
      })}
    </div>
  );
}
